import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const FORM_URL = "https://readdy.ai/api/form/db69iejghk82orf0cjl0";

type SubmitStatus = "idle" | "submitting" | "success" | "error";

export default function Feedback() {
  const [status, setStatus] = useState<SubmitStatus>("idle");
  const [errorMsg, setErrorMsg] = useState("");

  useDocumentTitle(
    "用户反馈 - 360直播 | 建议与问题反馈",
    "向360直播提交你的建议、问题与内容推荐，我们会认真阅读每一条反馈并持续改进。"
  );

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = ((formData.get("company_alt") as string) || "").trim();
    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === "string" && key !== "company_alt") {
        payload.append(key, value);
      }
    });

    // Silent bot trap: pretend success without sending anything.
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    setStatus("submitting");
    setErrorMsg("");

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === "OK") {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setErrorMsg(
          typeof serverMsg === "string" && serverMsg.trim()
            ? serverMsg
            : "提交失败，请稍后重试。"
        );
      }
    } catch {
      setStatus("error");
      setErrorMsg("网络异常，请检查网络后重试。");
    }
  };

  const inputClass =
    "h-11 w-full rounded-md border border-background-200 bg-background-50 px-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none";

  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 py-8 md:px-6 md:py-12">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "用户反馈" }]} />

      <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
        <div className="lg:col-span-5">
          <span className="mb-2 inline-flex items-center gap-1.5 text-xs font-label font-semibold uppercase tracking-[0.2em] text-primary-400">
            <span className="h-3 w-1 rounded-full bg-primary-500" />
            Feedback
          </span>
          <h1 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
            用户反馈
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-foreground-600">
            你的每一条建议，都是我们把 360直播做得更好的动力。无论是功能建议、内容推荐还是使用中遇到的问题，都欢迎告诉我们。
          </p>

          <div className="mt-8 flex flex-col gap-4">
            {[
              { icon: "ri-time-line", title: "响应及时", desc: "工作日 24 小时内回复你的反馈" },
              { icon: "ri-shield-check-line", title: "信息安全", desc: "你的信息仅用于处理本次反馈" },
              { icon: "ri-sparkling-line", title: "认真采纳", desc: "优质建议将纳入产品迭代计划" },
            ].map((item) => (
              <div key={item.title} className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-500/15 text-primary-300">
                  <i className={`${item.icon} text-lg`} />
                </span>
                <div>
                  <h2 className="text-sm font-bold text-foreground-950">{item.title}</h2>
                  <p className="mt-0.5 text-xs text-foreground-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <Link
            to="/"
            className="mt-8 inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-100 px-5 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-500/60"
          >
            <i className="ri-home-4-line" />
            返回首页
          </Link>
        </div>

        <div className="lg:col-span-7">
          <div className="rounded-xl border border-background-200/60 bg-background-100 p-6 md:p-8">
            {status === "success" ? (
              <div className="flex flex-col items-center py-10 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-500/15 text-primary-300">
                  <i className="ri-checkbox-circle-line text-4xl" />
                </span>
                <h2 className="mt-5 font-heading text-xl font-bold text-foreground-950">
                  感谢您的反馈！
                </h2>
                <p className="mt-3 max-w-sm text-sm text-foreground-600">
                  我们已经收到你的建议，会认真阅读并持续改进 360直播的体验。
                </p>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-add-line" />
                  再提交一条反馈
                </button>
              </div>
            ) : (
              <form data-readdy-form id="feedback-form" onSubmit={handleSubmit} className="flex flex-col gap-5">
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <label htmlFor="feedback-name" className="text-sm font-medium text-foreground-800">
                      姓名 <span className="text-accent-400">*</span>
                    </label>
                    <input
                      id="feedback-name"
                      name="name"
                      type="text"
                      required
                      placeholder="请输入你的称呼"
                      className={inputClass}
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label htmlFor="feedback-email" className="text-sm font-medium text-foreground-800">
                      联系方式 <span className="text-accent-400">*</span>
                    </label>
                    <input
                      id="feedback-email"
                      name="email"
                      type="email"
                      required
                      placeholder="请输入邮箱，便于我们回复"
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="feedback-type" className="text-sm font-medium text-foreground-800">
                    反馈类型
                  </label>
                  <div className="relative">
                    <select
                      id="feedback-type"
                      name="type"
                      defaultValue="功能建议"
                      className={`${inputClass} cursor-pointer appearance-none pr-10`}
                    >
                      <option value="功能建议">功能建议</option>
                      <option value="内容推荐">内容推荐</option>
                      <option value="问题反馈">问题反馈</option>
                      <option value="合作洽谈">合作洽谈</option>
                      <option value="其他">其他</option>
                    </select>
                    <i className="ri-arrow-down-s-line pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-foreground-500" />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="feedback-content" className="text-sm font-medium text-foreground-800">
                    反馈内容 <span className="text-accent-400">*</span>
                  </label>
                  <textarea
                    id="feedback-content"
                    name="content"
                    required
                    maxLength={500}
                    rows={6}
                    placeholder="请详细描述你的建议或问题（不超过 500 字）"
                    className="w-full resize-none rounded-md border border-background-200 bg-background-50 px-3 py-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
                  />
                  <p className="text-xs text-foreground-500">最多 500 字，请尽量描述清楚，方便我们处理。</p>
                </div>

                {/* anti-spam honeypot - hidden via stylesheet */}
                <input
                  type="text"
                  name="company_alt"
                  className="field-verify-hidden"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {status === "error" && (
                  <div className="flex items-start gap-2 rounded-md border border-accent-500/40 bg-accent-500/10 px-4 py-3 text-sm text-accent-300">
                    <i className="ri-error-warning-line mt-0.5" />
                    <span>{errorMsg}</span>
                  </div>
                )}

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {status === "submitting" ? (
                      <>
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-background-50/40 border-t-background-50" />
                        提交中…
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line" />
                        提交反馈
                      </>
                    )}
                  </button>
                  <p className="text-xs text-foreground-500">
                    提交即表示你同意我们使用该信息处理本次反馈。
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}